<template>
  <div class="login-shell">
    <div class="login-card">
      <!-- Panel izquierdo: marca -->
      <aside class="login-brand">
        <div class="login-brand__overlay"></div>
        <div class="login-brand__content">
          <div class="login-brand__logo">
            <img src="/static/logo-01.png" alt="Vanguard Transit Solutions" />
          </div>

          <h1 class="login-brand__title">Bienvenido de vuelta</h1>
          <p class="login-brand__subtitle">
            Ingresa tus credenciales para acceder a tu plataforma.
          </p>

          <ul class="login-brand__features">
            <li v-for="(f, i) in features" :key="i">
              <span class="login-brand__feature-icon">
                <v-icon dark size="18">{{ f.icon }}</v-icon>
              </span>
              <div>
                <strong>{{ f.title }}</strong>
                <small>{{ f.text }}</small>
              </div>
            </li>
          </ul>

          <div class="login-brand__accent"></div>
        </div>
      </aside>

      <!-- Panel derecho: formulario -->
      <main class="login-form-panel">
        <div class="login-form-panel__inner">
          <h2 class="login-form-panel__title">Iniciar sesión</h2>
          <p class="login-form-panel__subtitle">
            Accede con tu cuenta corporativa
          </p>

          <form class="login-form" @submit.prevent="login">
            <div class="form-field">
              <label for="login-email">Usuario o correo electrónico</label>
              <v-text-field
                id="login-email"
                v-model="form.email"
                data-vv-name="email"
                v-validate="'required|email'"
                :error-messages="errors.collect('email')"
                placeholder="Ingresa tu usuario o correo"
                solo
                flat
                hide-details
                class="login-input"
                @keyup.enter="login"
              ></v-text-field>
            </div>

            <div class="form-field">
              <label for="login-password">Contraseña</label>
              <v-text-field
                id="login-password"
                v-model="form.password"
                data-vv-name="password"
                v-validate="'required'"
                :error-messages="errors.collect('password')"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                placeholder="Ingresa tu contraseña"
                solo
                flat
                hide-details
                class="login-input"
                @keyup.enter="login"
              ></v-text-field>
            </div>

            <div class="login-form__forgot">
              <a href="#" @click.prevent="onForgotPassword"
                >¿No recuerdas tu contraseña?</a
              >
            </div>

            <v-btn
              type="submit"
              class="login-form__submit"
              :loading="loading"
              dark
              block
            >
              <v-icon left>lock</v-icon>
              Iniciar sesión
            </v-btn>

            <p class="login-form__signup">
              ¿Aún no tienes cuenta?
              <a href="#" @click.prevent="onContact">Contáctanos</a>
            </p>
          </form>
        </div>
      </main>
    </div>

    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      :timeout="4000"
      right
      top
    >
      <v-icon left dark small>{{
        snack.color === 'error' ? 'error' : 'info'
      }}</v-icon>
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data: () => ({
    loading: false,
    showPassword: false,
    form: {
      email: '',
      password: '',
    },
    snack: {
      show: false,
      color: 'info',
      message: '',
    },
    features: [
      {
        icon: 'verified_user',
        title: 'Información segura',
        text: 'Protegemos tus datos con los más altos estándares de seguridad.',
      },
      {
        icon: 'bolt',
        title: 'Acceso en tiempo real',
        text: 'Consulta y gestiona tu información al instante.',
      },
      {
        icon: 'dashboard',
        title: 'Control total',
        text: 'Todo lo que necesitas, en un solo lugar.',
      },
    ],
  }),

  methods: {
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    routeByRole(role) {
      if (role === 'TRA') return '/logistica/transportista'
      if (['MENSAJERO', 'ENT'].includes(role)) return '/logistica/mensajeria'
      if (this.$acl.isAdmin(role)) return '/dashboard'
      if (this.$acl.isAccounting(role)) return '/contabilidad/pago-de-impuestos'
      if (this.$acl.isDelivery(role)) return '/transit/entregas'
      if (
        this.$acl.isLaboratory(role) ||
        this.$acl.isDistributor(role) ||
        this.$acl.isClient(role)
      ) {
        return '/dashboard'
      }
      return '/transit/facturas'
    },
    async login() {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.loading = true
      try {
        const response = await this.$store.dispatch('session/login', {
          email: this.form.email,
          upass: this.form.password,
        })

        if (!response || response.status !== 200 || !response.data) {
          const msg =
            (response && response.data && response.data.error) ||
            'Credenciales inválidas. Verifica e intenta de nuevo.'
          this.notify(msg, 'error')
          return
        }

        const { role } = response.data
        this.$router.push(this.routeByRole(role))
      } catch (e) {
        this.notify(
          'No se pudo conectar al servidor. Intenta más tarde.',
          'error'
        )
      } finally {
        this.loading = false
      }
    },
    onForgotPassword() {
      this.notify(
        'Contacta al administrador del sistema para recuperar tu contraseña.',
        'info'
      )
    },
    onContact() {
      window.location.href = 'mailto:soporte@vanguardtransit.com'
    },
  },
}
</script>

<style scoped>
.login-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(
      ellipse at top right,
      rgba(37, 79, 135, 0.08),
      transparent 60%
    ),
    radial-gradient(
      ellipse at bottom left,
      rgba(229, 37, 42, 0.05),
      transparent 60%
    ),
    #f6f8fc;
}

.login-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: 1040px;
  min-height: 580px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

/* === Panel izquierdo (marca) === */
.login-brand {
  position: relative;
  background: linear-gradient(135deg, #15324f 0%, #0d1d33 100%);
  color: #ffffff;
  padding: 40px 36px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Avión silueta en el fondo (icono Material como pseudo-element) */
.login-brand::before {
  content: 'flight_takeoff';
  font-family: 'Material Icons';
  font-feature-settings: 'liga' 1;
  position: absolute;
  top: 6%;
  right: -10%;
  font-size: 320px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.06);
  transform: rotate(-18deg);
  pointer-events: none;
  z-index: 0;
}

/* Fade vertical: deja respirar el avión arriba y oscurece abajo para el contenido */
.login-brand::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    transparent 30%,
    #0d1d33 90%
  );
  pointer-events: none;
  z-index: 0;
}

.login-brand__overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(199, 154, 43, 0.12),
      transparent 50%
    ),
    radial-gradient(circle at 90% 10%, rgba(229, 37, 42, 0.1), transparent 45%);
  pointer-events: none;
  z-index: 1;
}

.login-brand__content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.login-brand__logo {
  margin-bottom: 36px;
  text-align: center;
}

.login-brand__logo img {
  max-width: 220px;
  width: 100%;
  height: auto;
}

.login-brand__title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #ffffff;
  letter-spacing: -0.01em;
}

.login-brand__subtitle {
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 32px;
  line-height: 1.5;
}

.login-brand__features {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.login-brand__features li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.login-brand__feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(199, 154, 43, 0.14);
  border: 1px solid rgba(199, 154, 43, 0.22);
  flex-shrink: 0;
}

.login-brand__features strong {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 2px;
}

.login-brand__features small {
  display: block;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.45;
}

.login-brand__accent {
  margin-top: auto;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, #e5252a 0%, #c79a2b 50%, #254f87 100%);
  opacity: 0.85;
}

/* === Panel derecho (formulario) === */
.login-form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 44px;
  background: #ffffff;
}

.login-form-panel__inner {
  width: 100%;
  max-width: 360px;
}

.login-form-panel__title {
  font-size: 26px;
  font-weight: 700;
  color: #172133;
  margin: 0 0 4px;
  letter-spacing: -0.01em;
}

.login-form-panel__subtitle {
  font-size: 13px;
  color: #5f6880;
  margin: 0 0 28px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-field label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #172133;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}

.login-form__forgot {
  text-align: right;
  margin-top: -4px;
  font-size: 12.5px;
}

.login-form__forgot a,
.login-form__signup a {
  color: #254f87;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.15s ease;
}

.login-form__forgot a:hover,
.login-form__signup a:hover {
  color: #e5252a;
}

.login-form__submit {
  margin: 12px 0 0 !important;
  padding: 0 16px !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  text-transform: none !important;
  height: 48px !important;
  min-height: 48px !important;
  width: 100% !important;
  background: #254f87 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(37, 79, 135, 0.2) !important;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease !important;
}

.login-form__submit:hover {
  background: #1f4373 !important;
  box-shadow: 0 8px 22px rgba(37, 79, 135, 0.32) !important;
  transform: translateY(-1px);
}

.login-form__submit:active {
  background: #1a3a63 !important;
  transform: translateY(0);
  box-shadow: 0 3px 10px rgba(37, 79, 135, 0.2) !important;
}

.login-form__submit /deep/ .v-btn__content {
  font-size: 15.5px;
  padding: 0 8px;
  height: 100%;
}

.login-form__submit /deep/ .v-icon {
  font-size: 20px !important;
  margin-right: 12px !important;
}

.login-form__signup {
  text-align: center;
  margin: 16px 0 0;
  font-size: 12.5px;
  color: #5f6880;
}

@media (max-width: 880px) {
  .login-shell {
    padding: 0;
    align-items: stretch;
    min-height: 100vh;
    background: #f6f8fc;
  }

  .login-card {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    border-radius: 0;
    box-shadow: none;
    min-height: 100vh;
    max-width: none;
  }

  /* === Brand mobile: solo el logo === */
  .login-brand {
    padding: 24px;
    min-height: auto;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .login-brand__content {
    align-items: center;
    text-align: center;
  }

  .login-brand__logo {
    margin: 0;
    text-align: center;
  }

  .login-brand__logo img {
    max-width: 170px;
  }

  /* Ocultar todo lo demás del brand */
  .login-brand__title,
  .login-brand__subtitle,
  .login-brand__features,
  .login-brand__accent {
    display: none;
  }

  .login-brand::before,
  .login-brand::after {
    display: none;
  }

  .login-brand__overlay {
    display: none;
  }

  /* === Form: ocupa el resto, fondo blanco === */
  .login-form-panel {
    padding: 24px 22px 28px;
    background: #ffffff;
    border-radius: 0;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .login-form-panel__inner {
    max-width: 100%;
    margin-top: 0;
  }

  .login-form-panel__title {
    font-size: 22px;
    text-align: center;
    margin-top: 0;
  }

  .login-form-panel__subtitle {
    text-align: center;
    margin-bottom: 20px;
  }

  .login-form__submit {
    height: 50px !important;
    font-size: 14.5px !important;
  }

  .login-form__signup {
    margin-top: 20px;
  }
}

@media (max-width: 380px) {
  .login-brand {
    padding: 18px;
  }

  .login-brand__logo img {
    max-width: 140px;
  }

  .login-form-panel {
    padding: 24px 18px 28px;
  }
}
</style>

<style>
/* === Estilos del input (fuera de scoped porque v-text-field los renderiza con clases globales) === */
.login-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e4e8f1 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  min-height: 46px !important;
  padding: 0 14px !important;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.login-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border-color: #c9d4e6 !important;
}

.login-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.1) !important;
}

.login-input.v-text-field--solo.error--text .v-input__control > .v-input__slot {
  border-color: #e5252a !important;
  box-shadow: 0 0 0 3px rgba(229, 37, 42, 0.08) !important;
}

.login-input input {
  font-size: 14px !important;
  color: #172133 !important;
}

.login-input input::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

.login-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 20px !important;
  transition: color 0.15s ease;
}

.login-input .v-input__icon .v-icon:hover {
  color: #254f87 !important;
}

.login-input .v-messages.error--text {
  margin-top: 4px;
  font-size: 11.5px !important;
}
</style>
